/**
 * Ward Labs W-1 report import.
 *
 * A Ward Labs PDF is the water report a US brewer actually holds: one page,
 * six brewing ions, alkalinity as CaCO₃. Typing it in is how residual alkalinity
 * silently goes wrong — the form already converts the alkalinity convention,
 * but it cannot know that Ward prints sulfate as **SO₄-S** (elemental sulfur),
 * which is a factor of three below the SO₄²⁻ every brewing calculation uses.
 * Importing the PDF is the only way to apply that conversion without the
 * brewer having to remember it.
 *
 * Everything happens in this tab. There is no upload: the file is inflated,
 * the shown text is read, and the numbers land in the same profile form a
 * typed report uses. The brewer still has to hit Save — an import is a draft,
 * not a write — so a misread ion is visible before it becomes a command.
 *
 * ## Why a dedicated reader rather than pdf.js
 *
 * Ward Labs has used the same Crystal-Reports-style template for decades:
 * FlateDecode content streams and `(label) Tj (value) Tj` pairs. That is a
 * small, testable surface. Pulling in a PDF renderer to recover twelve numbers
 * would be the toast-component mistake at a larger scale.
 */

import {
  alkalinityFromBicarbonate,
  bicarbonateFromAlkalinity,
  SULFATE_FROM_SULFUR,
} from '@beerwright/core';

/**
 * The SO₄-S → SO₄²⁻ ratio moved to core (`water.ts#SULFATE_FROM_SULFUR`) when
 * the assistant report reader became its second user; re-exported so this
 * module's callers keep one import for everything Ward.
 */
export { SULFATE_FROM_SULFUR } from '@beerwright/core';

/** The one thing this reader throws: input that is not a Ward Labs W-1 report. */
export class WardLabsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WardLabsError';
  }
}

/** Narrow an unknown catch variable. */
export function isWardLabsError(value: unknown): value is WardLabsError {
  return value instanceof WardLabsError;
}

/**
 * The brewing-relevant numbers recovered from a W-1 report.
 *
 * Identity fields (`id`, timestamps, `builtin`) are the caller's: this is a
 * parse, not a document. Alkalinity is already converted to bicarbonate so
 * the profile form can show it back as CaCO₃ without a second conversion.
 */
export interface WardLabsParse {
  readonly name: string;
  readonly notes: string;
  readonly calciumPpm: number;
  readonly magnesiumPpm: number;
  readonly sodiumPpm: number;
  readonly chloridePpm: number;
  readonly sulfatePpm: number;
  readonly bicarbonatePpm: number;
  readonly ph: number | null;
  readonly alkalinityEntryForm: 'caco3';
  /** The SO₄-S figure as printed, before conversion — for the review notice. */
  readonly sulfateAsSulfurPpm: number;
  /** Total alkalinity as CaCO₃ as printed. */
  readonly alkalinityAsCaco3Ppm: number;
  readonly labNumber: string | null;
  /** ISO `YYYY-MM-DD` of the date reported, when the PDF carried one. */
  readonly reportedOn: string | null;
}

/** Read a Ward Labs PDF into brewing ions. */
export async function readWardLabsPdf(bytes: Uint8Array): Promise<WardLabsParse> {
  if (!isPdf(bytes)) {
    throw new WardLabsError('This does not look like a PDF.');
  }
  const text = await extractPdfText(bytes);
  if (text === '') {
    throw new WardLabsError(
      "We couldn't find any text in that PDF. A scan or photo won't work, but the text PDF Ward Labs emails you will.",
    );
  }
  return parseWardLabsReport(text);
}

/** True when `bytes` start with the PDF magic. */
export function isPdf(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 5 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46 &&
    bytes[4] === 0x2d
  );
}

/**
 * Shown text from a simple PDF, in drawing order.
 *
 * Only FlateDecode and uncompressed content streams are read, and only those
 * that actually paint text (`Tj` / `TJ`). Font files, images and ToUnicode
 * CMaps are skipped. That is enough for a Ward Labs W-1 and for the tiny
 * fixtures the tests build; it is not a PDF renderer.
 */
export async function extractPdfText(bytes: Uint8Array): Promise<string> {
  const tokens: string[] = [];
  for (const payload of pdfStreamPayloads(bytes)) {
    const content = await decodeStream(payload);
    if (content === null) continue;
    const shown = shownText(content);
    if (shown.length > 0) tokens.push(...shown);
  }
  return tokens.join(' ');
}

/** Parse the shown text of a W-1 into brewing ions. */
export function parseWardLabsReport(text: string): WardLabsParse {
  const normalized = normalizeWardLabsText(text);
  if (!isWardLabsText(normalized)) {
    throw new WardLabsError('This PDF does not look like a Ward Labs water report.');
  }

  const calciumPpm = ppmAfter(normalized, 'Calcium, Ca');
  const magnesiumPpm = ppmAfter(normalized, 'Magnesium, Mg');
  const sodiumPpm = ppmAfter(normalized, 'Sodium, Na');
  const chloridePpm = ppmAfter(normalized, 'Chloride, Cl');
  const sulfateAsSulfurPpm = ppmAfter(normalized, 'Sulfate, SO4-S');
  const alkalinityAsCaco3Ppm = ppmAfter(normalized, 'Total Alkalinity, CaCO3');
  const bicarbonatePrinted = ppmAfter(normalized, 'Bicarbonate, HCO3');

  const missing: string[] = [];
  if (calciumPpm === null) missing.push('calcium');
  if (magnesiumPpm === null) missing.push('magnesium');
  if (sodiumPpm === null) missing.push('sodium');
  if (chloridePpm === null) missing.push('chloride');
  if (sulfateAsSulfurPpm === null) missing.push('sulfate');
  if (alkalinityAsCaco3Ppm === null && bicarbonatePrinted === null) missing.push('alkalinity');
  if (missing.length > 0) {
    throw new WardLabsError(`The report is missing ${missing.join(', ')}.`);
  }

  // The non-null assertions below are the missing-list above; TypeScript cannot
  // see that an empty `missing` means every required field landed.
  const sulfateS = sulfateAsSulfurPpm as number;
  const bicarbonatePpm =
    alkalinityAsCaco3Ppm !== null
      ? bicarbonateFromAlkalinity(alkalinityAsCaco3Ppm)
      : (bicarbonatePrinted as number);
  const alkalinity =
    alkalinityAsCaco3Ppm ?? alkalinityFromBicarbonate(bicarbonatePrinted as number);

  const labNumber = firstMatch(normalized, /Lab Number\s*:\s*(\d+)/);
  const reportedOn = reportedDate(normalized);
  const ph = phOf(normalized);

  return {
    name: profileName(labNumber, reportedOn),
    notes: profileNotes(labNumber, reportedOn),
    calciumPpm: roundPpm(calciumPpm as number),
    magnesiumPpm: roundPpm(magnesiumPpm as number),
    sodiumPpm: roundPpm(sodiumPpm as number),
    chloridePpm: roundPpm(chloridePpm as number),
    sulfatePpm: roundPpm(sulfateS * SULFATE_FROM_SULFUR),
    bicarbonatePpm,
    ph,
    alkalinityEntryForm: 'caco3',
    sulfateAsSulfurPpm: sulfateS,
    alkalinityAsCaco3Ppm: roundPpm(alkalinity),
    labNumber,
    reportedOn,
  };
}

/** One-line review copy the profile dialog shows above the form. */
export function wardLabsReviewNotice(parsed: WardLabsParse): string {
  const sulfur = formatPpm(parsed.sulfateAsSulfurPpm);
  const sulfate = formatPpm(parsed.sulfatePpm);
  return `We imported this from Ward Labs and converted sulfate from SO₄-S (${sulfur} ppm sulfur → ${sulfate} ppm SO₄²⁻). Check the numbers and save.`;
}

// ---------------------------------------------------------------------------
// Text normalisation and field reads
// ---------------------------------------------------------------------------

/**
 * Stitch the subscript splits a Ward Labs PDF emits.
 *
 * The template draws `SO₄-S` as three Tj strings — `(Sulfate, SO) (4) (-S)` —
 * because the 4 is a different font. Joining with spaces would leave
 * `Sulfate, SO 4 -S 24`, which is readable to a human and annoying to a
 * regex. Collapsing those known splits first lets every field use the same
 * "label then number" matcher.
 */
function normalizeWardLabsText(text: string): string {
  return text
    .replace(/SO\s*4\s*-S/g, 'SO4-S')
    .replace(/HCO\s*3/g, 'HCO3')
    .replace(/CaCO\s*3/g, 'CaCO3')
    .replace(/NO\s*3\s*-N/g, 'NO3-N')
    .replace(/CO\s*3/g, 'CO3')
    .replace(/\s+/g, ' ')
    .trim();
}

function isWardLabsText(text: string): boolean {
  const branded = /wardlab\.com/i.test(text) || /ward\s+lab/i.test(text);
  const layout = /Sulfate,\s*SO4-S/i.test(text) && /Total Alkalinity,\s*CaCO3/i.test(text);
  return branded || layout;
}

function ppmAfter(text: string, label: string): number | null {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = text.match(new RegExp(`${escaped}\\s*(<\\s*)?(\\d+(?:\\.\\d+)?)`, 'i'));
  if (match === null || match[2] === undefined) return null;
  const value = Number(match[2]);
  if (!Number.isFinite(value)) return null;
  // "< 1.0" is below detection; for a brewing ion that is zero, not a gap.
  if (match[1] !== undefined) return 0;
  return value;
}

function phOf(text: string): number | null {
  const match = text.match(/\bpH\s+(\d+(?:\.\d+)?)/);
  if (match === null || match[1] === undefined) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value) || value < 3 || value > 11) return null;
  return value;
}

function firstMatch(text: string, pattern: RegExp): string | null {
  const match = text.match(pattern);
  const captured = match?.[1];
  return captured === undefined || captured === '' ? null : captured;
}

/**
 * The date the lab reported, not the copy-printed date at the footer.
 *
 * The W-1 draws "Date Received" and "Date Reported" as a group of labels and
 * then the two dates in order, so the second `MM/DD/YYYY` in the file is the
 * reported date. The footer copy date is unpadded (`9/23/2020`) and comes
 * last; using the second date rather than the last is what avoids it.
 */
function reportedDate(text: string): string | null {
  const dates = [...text.matchAll(/(\d{1,2}\/\d{1,2}\/\d{2,4})/g)].map((match) => match[1]);
  const printed = dates[1] ?? dates[0];
  if (printed === undefined) return null;
  return toIsoDate(printed);
}

function toIsoDate(printed: string): string | null {
  const match = printed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (
    match === null ||
    match[1] === undefined ||
    match[2] === undefined ||
    match[3] === undefined
  ) {
    return null;
  }
  const month = Number(match[1]);
  const day = Number(match[2]);
  let year = Number(match[3]);
  if (year < 100) year += 2000;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function profileName(labNumber: string | null, reportedOn: string | null): string {
  if (labNumber !== null && reportedOn !== null) return `Ward Labs ${labNumber} — ${reportedOn}`;
  if (labNumber !== null) return `Ward Labs ${labNumber}`;
  if (reportedOn !== null) return `Ward Labs — ${reportedOn}`;
  return 'Ward Labs report';
}

function profileNotes(labNumber: string | null, reportedOn: string | null): string {
  const parts = ['Imported from a Ward Labs report.'];
  if (labNumber !== null) parts.push(`Lab ${labNumber}.`);
  if (reportedOn !== null) parts.push(`Reported ${reportedOn}.`);
  parts.push('Sulfate converted from SO₄-S.');
  return parts.join(' ');
}

function roundPpm(value: number): number {
  return Math.round(value * 10) / 10;
}

function formatPpm(value: number): string {
  return Number.isInteger(value) ? String(value) : String(roundPpm(value));
}

// ---------------------------------------------------------------------------
// PDF content streams
// ---------------------------------------------------------------------------

interface PdfStream {
  readonly dict: string;
  readonly data: Uint8Array;
}

function pdfStreamPayloads(bytes: Uint8Array): PdfStream[] {
  const source = latin1Decode(bytes);
  const streams: PdfStream[] = [];
  let cursor = 0;
  while (cursor < source.length) {
    const marker = indexOfStreamKeyword(source, cursor);
    if (marker === null) break;
    const dictStart = matchingDictStart(source, marker.keywordAt);
    if (dictStart === -1) {
      cursor = marker.dataAt;
      continue;
    }
    const dict = source.slice(dictStart, marker.keywordAt);
    const length = dictLength(dict);
    const dataEnd =
      length !== null
        ? marker.dataAt + length
        : (() => {
            const end = source.indexOf('endstream', marker.dataAt);
            return end === -1 ? source.length : end;
          })();
    streams.push({ dict, data: bytes.subarray(marker.dataAt, Math.min(dataEnd, bytes.length)) });
    const endstream = source.indexOf('endstream', dataEnd);
    cursor = endstream === -1 ? dataEnd : endstream + 'endstream'.length;
  }
  return streams;
}

/** Byte offset of the next `stream` keyword and of the bytes that follow it. */
function indexOfStreamKeyword(
  source: string,
  from: number,
): { readonly keywordAt: number; readonly dataAt: number } | null {
  const keywordAt = source.indexOf('stream', from);
  if (keywordAt === -1) return null;
  // PDF keywords are delimited; reject `stream` inside a name or a longer word.
  const before = keywordAt === 0 ? '' : source[keywordAt - 1];
  if (before !== undefined && before !== '' && !isPdfDelimiter(before)) {
    return indexOfStreamKeyword(source, keywordAt + 6);
  }
  let dataAt = keywordAt + 6;
  if (source[dataAt] === '\r') dataAt += 1;
  if (source[dataAt] === '\n') dataAt += 1;
  return { keywordAt, dataAt };
}

function isPdfDelimiter(char: string): boolean {
  return /[\s<>[\](){}/%]/.test(char);
}

/** Index of the `<<` that opens the dict immediately before `stream`. */
function matchingDictStart(source: string, keywordAt: number): number {
  let depth = 0;
  for (let index = keywordAt; index >= 1; index -= 1) {
    if (source[index] === '>' && source[index - 1] === '>') {
      depth += 1;
      index -= 1;
      continue;
    }
    if (source[index] === '<' && source[index - 1] === '<') {
      depth -= 1;
      if (depth === 0) return index - 1;
      index -= 1;
    }
  }
  return -1;
}

function dictLength(dict: string): number | null {
  const match = dict.match(/\/Length\s+(\d+)\b/);
  if (match === null || match[1] === undefined) return null;
  return Number(match[1]);
}

function isFlateDecode(dict: string): boolean {
  return /\/Filter\s*(?:\[\s*)?\/FlateDecode/.test(dict);
}

function isImageOrFont(dict: string): boolean {
  return /\/Subtype\s*\/Image/.test(dict) || /\/Length1\s/.test(dict);
}

async function decodeStream(stream: PdfStream): Promise<string | null> {
  if (isImageOrFont(stream.dict)) return null;
  if (isFlateDecode(stream.dict)) {
    try {
      const inflated = await inflateZlib(stream.data);
      const text = latin1Decode(inflated);
      return /Tj|TJ/.test(text) ? text : null;
    } catch {
      return null;
    }
  }
  const text = latin1Decode(stream.data);
  return /Tj|TJ/.test(text) ? text : null;
}

async function inflateZlib(data: Uint8Array): Promise<Uint8Array> {
  const copy = Uint8Array.from(data);
  try {
    return await inflateWith(copy, 'deflate');
  } catch {
    return inflateWith(copy, 'deflate-raw');
  }
}

async function inflateWith(
  // `ArrayBuffer`-backed specifically: `BlobPart` refuses a view that might sit
  // on a `SharedArrayBuffer`, and the copy `inflateZlib` makes satisfies that.
  data: Uint8Array<ArrayBuffer>,
  format: CompressionFormat,
): Promise<Uint8Array> {
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream(format));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Text painted by `Tj` / `TJ` / `'` / `"`.
 *
 * Literal strings only — Ward Labs does not use hex strings for labels, and
 * treating `<…>` as text would pick up binary in content streams that happen
 * to contain those bytes.
 */
function shownText(content: string): string[] {
  const tokens: string[] = [];
  let index = 0;
  while (index < content.length) {
    const char = content[index];
    if (char === '(') {
      const read = readLiteralString(content, index);
      if (read === null) {
        index += 1;
        continue;
      }
      index = read.next;
      const operator = nextOperator(content, index);
      if (operator !== null && isTextOperator(operator.name)) {
        tokens.push(read.value);
        index = operator.next;
      }
      continue;
    }
    if (char === '[') {
      const array = readTjArray(content, index);
      if (array !== null) {
        tokens.push(...array.values);
        index = array.next;
        continue;
      }
    }
    index += 1;
  }
  return tokens;
}

function isTextOperator(name: string): boolean {
  return name === 'Tj' || name === "'" || name === '"';
}

function nextOperator(content: string, from: number): { name: string; next: number } | null {
  const match = content.slice(from).match(/^\s*(\/[A-Za-z]+|[A-Za-z']+|")/);
  if (match === null || match[1] === undefined) return null;
  return { name: match[1], next: from + match[0].length };
}

function readTjArray(content: string, from: number): { values: string[]; next: number } | null {
  if (content[from] !== '[') return null;
  const values: string[] = [];
  let index = from + 1;
  while (index < content.length) {
    const char = content[index];
    if (char === ']') {
      const operator = nextOperator(content, index + 1);
      if (operator === null || operator.name !== 'TJ') return null;
      return { values, next: operator.next };
    }
    if (char === '(') {
      const read = readLiteralString(content, index);
      if (read === null) return null;
      values.push(read.value);
      index = read.next;
      continue;
    }
    index += 1;
  }
  return null;
}

function readLiteralString(source: string, from: number): { value: string; next: number } | null {
  if (source[from] !== '(') return null;
  let value = '';
  let depth = 1;
  let index = from + 1;
  while (index < source.length) {
    const char = source[index];
    if (char === '\\') {
      const escaped = unescapePdf(source, index);
      value += escaped.value;
      index = escaped.next;
      continue;
    }
    if (char === '(') {
      depth += 1;
      value += char;
      index += 1;
      continue;
    }
    if (char === ')') {
      depth -= 1;
      if (depth === 0) return { value, next: index + 1 };
      value += char;
      index += 1;
      continue;
    }
    value += char;
    index += 1;
  }
  return null;
}

function unescapePdf(source: string, from: number): { value: string; next: number } {
  const next = source[from + 1];
  if (next === undefined) return { value: '\\', next: from + 1 };
  switch (next) {
    case 'n':
      return { value: '\n', next: from + 2 };
    case 'r':
      return { value: '\r', next: from + 2 };
    case 't':
      return { value: '\t', next: from + 2 };
    case 'b':
      return { value: '\b', next: from + 2 };
    case 'f':
      return { value: '\f', next: from + 2 };
    case '(':
    case ')':
    case '\\':
      return { value: next, next: from + 2 };
    case '\n':
      return { value: '', next: from + 2 };
    case '\r':
      return { value: '', next: source[from + 2] === '\n' ? from + 3 : from + 2 };
    default:
      break;
  }
  if (next >= '0' && next <= '7') {
    let digits = next;
    let consumed = 1;
    while (consumed < 3) {
      const digit = source[from + 1 + consumed];
      if (digit === undefined || digit < '0' || digit > '7') break;
      digits += digit;
      consumed += 1;
    }
    return {
      value: String.fromCharCode(Number.parseInt(digits, 8) & 0xff),
      next: from + 1 + consumed,
    };
  }
  return { value: next, next: from + 2 };
}

function latin1Decode(bytes: Uint8Array): string {
  let text = '';
  const chunk = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    const slice = bytes.subarray(offset, offset + chunk);
    text += String.fromCharCode(...slice);
  }
  return text;
}
